/**
 * 最终 payload 组装：业务参数 + 全局字段（按 `globalsProfile` 档位条件注入）。
 *
 * 阶段 2（2026-10-05）起，全局字段不再是固定 5 个：档位由**事件定义声明的自有参数个数**静态决定
 * （`full` 5 个 / `standard` 3 个 / `minimal` 2 个），并恒发一个 `globals_profile` 标记。
 * 档位表与判定规则全部在 `./globalsProfile`（零 import 的纯数据模块，静态门也直接加载它）。
 *
 * ⚠️ 设备维度自 2026-10-05 起由 BQ 侧从 `user_agent` 派生（替代已退役的 `device_type`），
 * 写法见本文件末尾注释。
 */
import { runtimeEnv } from '@/config/featureFlags';
import {
  GLOBALS_BY_PROFILE,
  GLOBALS_PROFILE,
  GLOBALS_PROFILE_DEFAULT,
  GLOBALS_PROFILE_MARKER_KEY,
  isGlobalsProfile
} from './globalsProfile';

/**
 * 「公开字段」= **每一档都包含**的那两个全局字段（`timestamp` / `environment`），
 * 即方案 §4.2 里标为 P0 保护、永不条件化的字段。
 *
 * ⚠️ 直接引用 minimal 档列表，不要再手写第二份 —— 两份清单必然会漂移。
 */
export const PUBLIC_PARAM_KEYS = GLOBALS_BY_PROFILE[GLOBALS_PROFILE.MINIMAL];

/** 所有档位涉及到的全局字段并集（dev 期提示"档位外全局字段被显式传入"用） */
const ALL_PROFILE_GLOBAL_KEYS = new Set(Object.values(GLOBALS_BY_PROFILE).flat());

/**
 * 已退役的全局字段：**一律不发送**（治理方案 §3.1 / 字典 §8）。
 *
 * 两者都是零信息冗余，却各占「全站每个事件」1 个参数槽
 * （GA4 单事件总参数硬上限 36 = 自有 25 + GA4 自动 7~11，超限静默丢弃）：
 *   - `event_category`：按事件分组 `COUNT(DISTINCT event_category)` 恒为 1，与 `event_name` 1:1
 *     ⇒ 语义 = 事件注册表里的 `category` 字段，BQ 侧可由 `event_name` 完全推导。
 *     ⚠️ 事件定义里的 `category` 元数据**保留**——它是注册表信息，不占参数槽。
 *   - `device_type`：与 `user_agent` 同时存在的 13,408/13,408 行取值一致（100.00%）
 *     ⇒ 是 UA 的纯派生量；且 GA4 原生 `device.category` 覆盖 100% 可兜底。
 *     ⚠️ `user_agent` 必须保留：它是唯一真值源，且 UA 长 100 字符 > user property 值上限 36 字符，
 *     不能下沉为 user property。
 *
 * 通用原则：**删派生量是零信息损失，删真值源不可逆。**
 * 设备维度的 BQ 派生写法见本文件末尾注释。
 */
export const RETIRED_GLOBAL_PARAM_KEYS = Object.freeze([
  'event_category',
  'device_type'
]);

const RETIRED_GLOBAL_PARAM_KEY_SET = new Set(RETIRED_GLOBAL_PARAM_KEYS);

function isEmptyValue(value) {
  return value === undefined || value === null || value === '';
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * 组装最终 payload。
 *
 * @param {object} params 业务参数（已由 `sanitizeAnalyticsParams` 清洗）
 * @param {object} [options]
 * @param {string} [options.globalsProfile] 全局字段档位（`full` / `standard` / `minimal`）。
 *   由调用方 `analyticsService` 按**事件定义的声明**解析后传入；本函数不读运行时条数、
 *   不做动态裁剪（方案 §4.6 硬约束 1：档位必须静态决定）。非法/缺失值回退 `full`。
 * @param {Record<string, unknown>} [options.constants] 阶段 3：**事件级常量**声明
 *   （`{ 键: 该键在整个事件上的唯一取值 }`）。这些键**一律不进入 payload** ——
 *   取值由事件名唯一决定，属注册表元数据，分析侧用 `event_name` 映射还原（方案 §3.6.2 类型 A）。
 *   若实际传入值与该常量不一致 ⇒ dev 告警：说明"常量"判断有误，必须回退。
 * @param {Record<string, string>} [options.redundantParams] 阶段 3：**恒等冗余字段**声明
 *   （`{ 被丢弃的键: 保留的等价键 }`）。被丢弃键**一律不进入 payload**；若它存在而等价键缺席
 *   ⇒ dev 告警：那意味着真的丢了信息（方案 §3.6.2 类型 B）。
 */
export function buildAnalyticsPayload(params = {}, options = {}) {
  const profile = isGlobalsProfile(options.globalsProfile)
    ? options.globalsProfile
    : GLOBALS_PROFILE_DEFAULT;
  const allowedGlobals = new Set(GLOBALS_BY_PROFILE[profile]);
  // 阶段 3：事件级下沉声明（由 analyticsService 从事件定义解析后传入；本函数不读注册表）
  const sunkConstants = isPlainObject(options.constants) ? options.constants : null;
  const redundantParams = isPlainObject(options.redundantParams) ? options.redundantParams : null;

  const base = {};

  for (const [key, value] of Object.entries(params)) {
    // 退役字段即使被调用方显式传入也不发送（否则参数槽会被悄悄花掉）；
    // dev 期告警，避免"传了但没上"变成静默失效。
    if (RETIRED_GLOBAL_PARAM_KEY_SET.has(key)) {
      if (runtimeEnv.isDevelopment) {
        console.warn(
          `[Analytics] Retired param dropped: ${key}（已退役，见 doc/开发标准/analytics_事件参数治理方案 §3.1）`
        );
      }
      continue;
    }

    // ── 阶段 3：事件级参数下沉（常量 / 恒等冗余）──
    // 两类键都**不得进入 payload**：前者取值由事件名唯一决定，后者与另一字段恒等。
    // 单一执行点 + 强制剥离 ⇒ 调用方即使漏删也不会把参数槽悄悄花掉（防回归）。
    if (sunkConstants && hasOwn(sunkConstants, key)) {
      if (runtimeEnv.isDevelopment && !isEmptyValue(value) && value !== sunkConstants[key]) {
        console.warn(
          `[Analytics] Sunk constant mismatched: ${key}=${JSON.stringify(value)}` +
            `（声明为 ${JSON.stringify(sunkConstants[key])}）` +
            '—— 该字段并非事件级常量，下沉判断有误，须回退（方案 §3.6.2 类型 A）'
        );
      }
      continue;
    }

    if (redundantParams && hasOwn(redundantParams, key)) {
      const survivor = redundantParams[key];
      if (runtimeEnv.isDevelopment && !isEmptyValue(value) && isEmptyValue(params[survivor])) {
        console.warn(
          `[Analytics] Redundant param dropped without survivor: ${key}` +
            `（本应等价于 ${survivor}，但后者缺席）—— 此条**可能真的丢了信息**（方案 §3.6.2 类型 B）`
        );
      }
      continue;
    }

    // 档位外的全局字段被显式传入：**不剥离**（显式值优先于全局兜底），但 dev 期提示 ——
    // 否则「标注 minimal 的事件实际带了 user_agent」会悄悄破坏「档位 ⇔ 全局字段集合」
    // 这个不变式，而分析侧正是靠它解释"字段为什么缺席"。
    if (runtimeEnv.isDevelopment && ALL_PROFILE_GLOBAL_KEYS.has(key) && !allowedGlobals.has(key)) {
      console.warn(
        `[Analytics] Global param outside profile: ${key}（当前档位 ${profile} 不含它，已按显式值发送；见方案 §4.3）`
      );
    }

    if (!isEmptyValue(value)) {
      base[key] = value;
    }
  }

  // ── 全局字段：按档位白名单注入（方案 §4.3 / §5 阶段 2）──
  // `timestamp` / `environment` 在**每一档**里都在（§4.2 标为 P0 保护），且**无条件覆盖**
  // 调用方传入值：它们是唯一可信来源，缺失会把 dev 流量混进 prod、或造成"全站缺失"类误判。
  if (allowedGlobals.has('timestamp')) {
    base.timestamp = new Date().toISOString();
  }
  if (allowedGlobals.has('environment')) {
    base.environment = runtimeEnv.nodeEnv;
  }

  // 用户代理：设备/系统/浏览器识别与设备维度派生的**唯一真值源**。
  // ⚠️ 它**不能**下沉为 user property（UA 实测 100 字符 > user property 值上限 36 字符，§4.4.1），
  // 因此重事件（minimal 档）会**按设计丢失** UA —— 这是刻意的取舍，由 `globals_profile` 标识出来。
  if (allowedGlobals.has('user_agent') && typeof navigator !== 'undefined') {
    const ua = navigator.userAgent || '';
    if (ua && !base.user_agent) {
      base.user_agent = ua;
    }
  }

  // 浏览器语言：原始 navigator.language（如 "zh-CN" / "en-US" / "ja-JP"），
  // 注意这是用户浏览器原始标识，而非经过 i18n 映射后的三种产品语言（en/zh/ja）
  if (allowedGlobals.has('browser_language') && typeof navigator !== 'undefined') {
    const browserLanguage = navigator.language || '';
    if (browserLanguage && !base.browser_language) {
      base.browser_language = browserLanguage;
    }
  }

  // 用户时区：IANA 时区标识（如 "Asia/Shanghai" / "America/New_York"），
  // 与环境/设备同属技术环境维度，独立于产品 i18n 语言设置
  if (allowedGlobals.has('timezone') && typeof Intl !== 'undefined') {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (timeZone && !base.timezone) {
      base.timezone = timeZone;
    }
  }

  // 档位标记：**恒发**，且覆盖调用方传入值 —— 档位由事件定义静态决定，不允许被参数伪造，
  // 否则分析侧赖以解释"字段为什么缺席"的依据就失效了（方案 §4.6 硬约束 2）。
  base[GLOBALS_PROFILE_MARKER_KEY] = profile;

  return base;
}

// ── 设备维度：自 2026-10-05 起由 BQ 侧从 user_agent 派生（替代已退役的 device_type） ──
// 与旧 `resolveDeviceType()`（analyticsPayload.js 历史实现）**逐值等价**，实测 13,408/13,408 一致：
//
//   CASE
//     WHEN REGEXP_CONTAINS(LOWER(user_agent), r'ipad')   THEN 'ipad'
//     WHEN REGEXP_CONTAINS(LOWER(user_agent), r'iphone') THEN 'iphone'
//     WHEN REGEXP_CONTAINS(LOWER(user_agent), r'android') THEN 'android'
//     WHEN REGEXP_CONTAINS(LOWER(user_agent), r'mobile|windows phone') THEN 'mobile'
//     ELSE 'desktop'
//   END AS device_type_derived
//
// 粗粒度场景可直接用 GA4 原生 `device.category`（mobile/desktop/tablet，覆盖 100%）。
// ⚠️ 必须封装为 BQ 视图/函数，不要在每个分析里各写一遍。
